import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, takeUntil } from 'rxjs/operators';

import { isString, SBaseDisplay } from '@summit/core';
import { DisplaySchema, FilterParameter, isGuid, isNumber, SummitIdObject } from '@summit/models';
import { CrudCacheItem, CrudCacheParameters, SummitCrudService } from '@summit/services';

import { SFormAutoCompleteComponent } from '../components/s-form-autocomplete/s-form-autocomplete.component';
import { SFormInputSelectComponent } from '../components/s-form-input-select/s-form-input-select.component';
import { SummitControl } from './summit-control';

const debugging = false;
@Directive()
export abstract class SummitItemControl<T extends SummitIdObject, U extends SummitIdObject>
  extends SummitControl<T>
  implements OnInit, AfterViewInit
{
  // if deselect is true, then entry will not automatically select the first entry (if items.length === 1)
  @Input() deselect = false;
  @Input() filter$: Observable<(string | number)[]> = of([]);
  @ViewChild('auto', { static: false }) auto?: SFormAutoCompleteComponent<T, U>;
  @ViewChild('control', { static: false }) control?: SFormInputSelectComponent<T>;
  @ViewChild('elementRef', { static: false }) elementRef?: ElementRef;
  @Output() dataLoaded = new EventEmitter<U[]>();
  @Input() defaultText?: string;
  protected crud: SummitCrudService;

  //Used to disable the Crud get on the item and use what is already in Items
  useLocalData = false;
  cacheParametersBs = new BehaviorSubject<CrudCacheParameters | null>(null);
  cacheParameters$ = this.cacheParametersBs.asObservable();
  filterParameters: FilterParameter[] = [];
  selectedItem?: U;
  activeControl?: SFormInputSelectComponent<T> | SFormAutoCompleteComponent<T, U>;
  public override cd: ChangeDetectorRef;
  protected itemsBs: BehaviorSubject<Array<U>> = new BehaviorSubject<Array<U>>([]);
  protected rawItems$: Observable<Array<U>> = this.itemsBs.asObservable(); //public so can you reference base
  items$: Observable<Array<U>> = this.rawItems$; //public so you can override is sub classes
  filteredItems$: Observable<Array<U>>;
  abstract selectObjectSchema: DisplaySchema<U>;
  apiFunction?: string;
  sortBy?: keyof U & string;
  convertDataToItem(data: Partial<U>): U {
    return this.selectObjectSchema.NewObject(data) as U;
  }
  constructor() {
    super();
    this.crud ??= inject(SummitCrudService);
    this.cd ??= inject(ChangeDetectorRef);
  }
  override ngOnInit() {
    this.filteredItems$ = combineLatest([this.items$, this.filter$]).pipe(
      //tap(([items, filter]) => console.log(`${this.fieldName.toString()}`, items, filter)),
      map(([items, filter]) => items?.filter((item) => this.customObservableFilter(item, filter))),
      //tap((items) => console.log(`${this.fieldName.toString()}: #2 Item Count ${items.length}`)),
      map((items) => items?.filter((item) => this.customFilter(item))),
      //tap((items) => console.log(`${this.fieldName.toString()}: #3 Item Count ${items.length}`))
      //tap(items => console.log(`Filtered Items: has Robert: ${items.filter(entry => entry.id == '1fba4856-fef3-4251-8066-f2f074ff4ff6').length > 0}`, items))
      shareReplay(1)
    );
    super.ngOnInit();
    if (!this.fieldName) {
      console.error(`[Summit Item Control] Missing fieldName`, this);
      return;
    }
    if (this.form !== undefined) {
      if (this.form) {
        const lclControl = this.form.controls[this.fieldName];
        if (!lclControl) {
          console.error(`Form doesn't have control of ${this.fieldName}`);
        } else {
          if (lclControl.value || lclControl.value == 0) {
            //console.log(`${this.fieldName}: Calling Load Current value if Missing`);
            this.loadCurrentValueIfMissing(); // in case it was set before init
          }
          lclControl.valueChanges
            .pipe(
              filter((_) => !this.cacheParametersBs?.value?.isLoading),
              distinctUntilChanged(),
              takeUntil(this.disposing$)
            )
            .subscribe((_) => {
              //console.log('???',_)
              // Only look up if it is patch and not user
              if (lclControl.pristine) {
                if (debugging)
                  console.log(`[control change sub] ${String(this.fieldName)}: Calling Load Current value if Missing`);
                this.loadCurrentValueIfMissing();
              }
            });
        }
      }
    } else {
      console.error(`[${this.fieldName}] Control didn't receive a Form Group`, typeof this);
    }

    this.configureFilterParameters();
    if (!this.useLocalData) this.getListData();
  }

  ngAfterViewInit(): void {
    if (this.control) {
      // console.log('Setting activeControl to select')
      this.activeControl = this.control;
    } else if (this.auto) {
      // console.log('Setting activeControl to auto', this.auto);
      this.activeControl = this.auto;
    }
    //console.log(`[AfterViewInit] ${this.fieldName}: Calling Load Current value if Missing`);
    this.loadCurrentValueIfMissing();
  }

  protected configureFilterParameters() {
    if (this.selectObjectSchema?.refFilterParameters) {
      this.filterParameters = this.selectObjectSchema.refFilterParameters.map((data) => new FilterParameter(data));
    }
    return;
  }

  protected getListData() {
    const debugging = false; // this.fieldName == 'routeGroupIds';
    // Handle Special Api functions
    let api = this.selectObjectSchema?.refServerAPI;
    if (this.apiFunction) {
      api += `/${this.apiFunction}`;
    }
    if (!api) {
      return;
    }
    // Get
    // console.log(`Pre crud Filter: ${this.filter.join(',')} to `, this.items);
    if (this.crud) {
      this.crud
        .get(api, null, null, this.filterParameters)
        .pipe(takeUntil(this.disposing$))
        .subscribe((result) => {
          if (!result) {
            return;
          }
          if (debugging) {
            console.log(`${this.fieldName?.toString()}: Received List data \n${JSON.stringify(result.data, null, 2)}`);
          }
          const resultItem = result as CrudCacheItem<U>;
          if (resultItem !== undefined && resultItem !== null) {
            this.cacheParametersBs.next(resultItem.parameters);
            this.itemsBs.next(
              resultItem.data && Array.isArray(resultItem.data)
                ? resultItem.data.map((data: U) => this.convertDataToItem(data)).filter((item: U) => item != null)
                : []
            );
          }
          if (debugging)
            console.log(`${this.fieldName?.toString()}: published ${this.itemsBs.value.length}`, this.itemsBs.value);
          this.loadCurrentValueIfMissing();
          this.addItemData();
          this.sortData();
          // console.log(`${this.fieldName}: Using List with name of 0`, this.items$.filter(item => item.name === '0'));
          this.updateComplete();
          this.cd.markForCheck();
        });
    }
  }

  customObservableFilter(item: U, filter: (string | number)[]): boolean {
    if (item.id == null) return true;
    return filter == null || filter.indexOf(item.id) < 0;
  }
  customFilter(_: unknown): boolean {
    return true;
  }

  protected loadCurrentValueIfMissing() {
    if (!this.fieldName || !this.form) return;
    const searchValue = this.form.controls[this.fieldName.toString()]?.value;
    if (!(isGuid(searchValue) || isString(searchValue) || isNumber(searchValue))) return;
    const cacheParameters = this.cacheParametersBs?.value;
    if (!cacheParameters || cacheParameters.isLoading) return;
    if (!this.selectedItem && (this.selectedItem as unknown as number) !== 0 && (searchValue || searchValue === 0)) {
      const index = this.itemsBs.value.findIndex((item) => this.getPerfectMatch(searchValue.toString(), item));
      // console.log(
      //   `[Summit Item Control] ${this.fieldName.toString()}: loadCurrentValueIfMissing: ${searchValue} index = ${index}`
      // );
      if (index < 0) {
        this.getAndAddItemData(searchValue);
      } else {
        const newSelItem = this.itemsBs.value[index];
        this.selectedItem = this.convertDataToItem(newSelItem);
        cacheParameters.isLoading = false;
        this.cacheParametersBs.next(cacheParameters);
        this.addItemData();
        this.sortData();
        this.setValue(searchValue); //Get display in sync with value
        this.updateComplete();
      }
    }
  }

  getPerfectMatchTest(): (search: string, item: U) => boolean {
    return (search: string, item: U) => {
      return item.id?.toString() === search;
    };
  }

  getPerfectMatch(search: string, item: U): boolean {
    const test = this.getPerfectMatchTest();
    if (test) {
      return test(search, item);
    }
    return false;
  }

  getAndAddItemData(id: string) {
    if (!id) return;
    //console.log(`[Summit Item Control] ${this.fieldName.toString()}: Getting Item Data for ${id}`);
    const cacheParameters = this.cacheParametersBs.value ?? new CrudCacheParameters({ isLoading: false });
    cacheParameters.isLoading = true;
    this.cacheParametersBs.next(cacheParameters);
    // Handle Special Api functions
    let api = this.selectObjectSchema.refServerAPI;
    if (this.apiFunction) {
      api += `/${this.apiFunction}`;
    }
    if (!api) {
      cacheParameters.isLoading = false;
      this.cacheParametersBs.next(cacheParameters);
      return;
    }
    //Fetch Data
    if (this.crud) {
      if (debugging) {
        console.log(`${String(this.fieldName)}: getting ${api} : ${id}`);
      }
      this.crud
        .get(api, id)
        .pipe(takeUntil(this.disposing$))
        .subscribe(
          (result) => {
            if (result) {
              const resultItem = result as CrudCacheItem<U>;
              if (debugging) {
                console.log(`[Summit Item Control] ${this.fieldName?.toString()}: Received Item data`, resultItem);
              }
              const currentCache = this.cacheParametersBs.value ?? new CrudCacheParameters({ isLoading: false });
              currentCache.isLoading = resultItem.parameters.isLoading;
              if (debugging) {
                console.log(`[Summit Item Control] Update CacheParameter's loading to: ${currentCache.isLoading}`);
              }
              this.cacheParametersBs.next(currentCache);
              if (debugging) {
                console.log(`[Summit Item Control] selected item = ${this.selectedItem}`);
              }
              if (!this.selectedItem && resultItem.parameters.isLoading === false) {
                if (debugging) {
                  console.log(`${this.fieldName?.toString()}: Item data`, resultItem.data);
                }
                if (!Array.isArray(resultItem.data)) this.selectedItem = this.convertDataToItem(resultItem.data);
                this.addItemData();
                this.sortData();
                if (debugging) {
                  console.log(`${this.fieldName?.toString()}: Using List`, this.itemsBs.value);
                }
                this.setValue(id); //Get display in sync with value
                this.updateComplete();
              }
            }
          },
          (error) => console.error(error)
        );
    }
  }

  addItemData() {
    const selectedItem = this.selectedItem;
    if (selectedItem && this.itemsBs.value.findIndex((item) => item.id === selectedItem.id) < 0) {
      if (debugging) {
        console.log(`[Summit Item Control] ${String(this.fieldName)}: Adding Item`, selectedItem);
      }
      const newList = [...this.itemsBs.value, selectedItem];
      this.itemsBs.next(newList);
      //console.log(`[Summit Item Control] ${this.fieldName}: Add Item`, this.items);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async updateComplete() {
    // Guard against undefined observable to prevent "You provided 'undefined' where a stream was expected" error
    if (!this.filteredItems$) {
      console.warn(`[Summit Item Control] ${this.fieldName?.toString()}: filteredItems$ is not initialized`);
      this.dataLoaded.emit([]);
      return;
    }
    try {
      this.dataLoaded.emit(await firstValueFrom(this.filteredItems$));
    } catch (error) {
      console.error(`[Summit Item Control] ${this.fieldName?.toString()}: Error getting filtered items`, error);
      this.dataLoaded.emit([]);
    }
  }

  getObject(id?: string | number): U | undefined {
    if (id || id === 0) {
      //console.log(`getting via id: ${id}`);
      return this.itemsBs.value.find((entry: U) => entry.id?.toString() === id.toString());
    }
    if (!this.fieldName) return undefined;
    const fieldName = this.fieldName;
    const controlValue = this.form?.controls[fieldName.toString()]?.value;
    //console.log(`getting via value: ${this.form?.controls[this.fieldName.toString()]?.value}`);
    return this.itemsBs.value.find((entry: U) => entry.id?.toString() === controlValue?.toString());
  }

  getAllObjects(): U[] {
    return this.itemsBs.value;
  }
  setAllObjects(items: U[]) {
    this.itemsBs.next(items);
  }

  setValue(id: string | number) {
    if (this.activeControl) {
      //console.log(`[Summit Item Control] ${this.fieldName}: Setting Value to ${id}`);
      this.activeControl.setValue(id);
      //console.log(`[Summit Item Control] ${this.fieldName}: Value set to ${this.activeControl.getValue()}`);
    }
  }

  sortData() {
    //console.log(`[Summit Item Control] ${this.fieldName}: Sorting Data`);
    const sortBy = this.sortBy;
    if (sortBy) {
      this.itemsBs.next(
        this.itemsBs.value.sort((n1, n2) => {
          if (n1[sortBy] > n2[sortBy]) {
            return 1;
          }
          if (n1[sortBy] < n2[sortBy]) {
            return -1;
          }
          return 0;
        })
      );
    } else if (this.itemsBs.value.length > 0 && this.itemsBs.value[0]['toShortString']) {
      this.itemsBs.next(
        this.itemsBs.value.sort((n1: unknown, n2: unknown) => {
          if (n1 instanceof SBaseDisplay && n2 instanceof SBaseDisplay) {
            if (n1.toShortString() > n2.toShortString()) {
              return 1;
            }
            if (n1.toShortString() < n2.toShortString()) {
              return -1;
            }
          }
          return 0;
        })
      );
    }
  }

  /* pulled from autoText - might need to be improved*/

  get createObject(): boolean {
    //console.log('get createObject', this.activeControl);
    if (this.activeControl instanceof SFormAutoCompleteComponent) {
      //console.log('get createObject: calling child');
      const response = this.activeControl.createObject;
      //console.log('get createObject: returning', response);
      return response;
    }
    return false;
  }

  set createObject(value: boolean) {
    if (this.activeControl instanceof SFormAutoCompleteComponent) {
      this.activeControl.createObject = value;
    }
  }

  selectNewEntry(newItem: U) {
    let itemEntry = this.itemsBs.value.find((entry: U) => entry.id === newItem.id);
    if (!itemEntry) {
      this.itemsBs.value.push(newItem);
      this.itemsBs.next(this.itemsBs.value);
      itemEntry = newItem;
    }
    if (itemEntry.id != null) {
      this.activeControl?.setValue(itemEntry.id);
    }
    if (this.activeControl instanceof SFormAutoCompleteComponent) {
      this.activeControl.updateSelection();
    }
  }
}
